import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { VersionBadge } from '@/components/common/VersionBadge';
import {
  IconDashboard,
  IconCalendar,
  IconUsers,
  IconBed,
  IconLuggage,
  IconFileText,
  IconForms,
  IconSettings,
  IconChevronLeft,
  IconChevronRight,
  IconChevronDown,
  IconX,
  IconKey,
  IconUserCircle,
  IconAdjustments,
  IconBook,
  IconMessageChatbot,
  IconRobot,
  IconBell,
  IconTool,
  IconTemperature,
  IconDatabaseImport,
  IconBug,
  IconGitPullRequest,
  IconUpload,
  IconListDetails,
  IconWorld,
  IconCoin,
  IconBuildingBank,
  IconShoppingCart,
  IconCast,
  IconPhoneOff,
  IconPhonePlus,
  IconNotebook,
  IconHistory,
  IconBrandWhatsapp,
  IconCreditCard,
  IconFileInvoice,
} from '@tabler/icons-react';
import { MessageCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { usePendingWorkflowCount } from '@/hooks/usePendingWorkflows';
import { usePrecontactBookingCount } from '@/hooks/usePrecontactBooking';
import { usePrecontactCount } from '@/hooks/usePrecontactCandidates';
import { useWhatsappInboxOpenCount } from '@/hooks/useWhatsappInbox';

interface SidebarProps {
  open: boolean;
  minimized: boolean;
  onClose: () => void;
  onToggleMinimize: () => void;
}

/** Comptadors que es resolen en temps de render, cadascun amb el seu hook. */
type DynamicBadge = 'workflows' | 'precontact' | 'precontactBooking' | 'whatsapp';

interface MenuItem {
  icon: any;
  label: string;
  path: string;
  disabled?: boolean;
  /** Com el dels grups: fora del menú per a qui no és ADMIN. */
  adminOnly?: boolean;
  /** Comptador fix. Per als que depenen de dades, fes servir `dynamicBadge`. */
  badge?: number;
  dynamicBadge?: DynamicBadge;
  /** També actiu a les subrutes (`/whatsapp/:conversationId`). */
  matchSubpaths?: boolean;
}

interface MenuGroup {
  label: string;
  icon: any;
  items: MenuItem[];
  adminOnly?: boolean;
  defaultOpen?: boolean;
}

// Menú principal (sempre visible)
const mainMenuItems: MenuItem[] = [
  { icon: IconDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: IconCalendar, label: 'Calendari', path: '/calendar-v2' },
  // Només ADMIN (D7 de `plans/legacy-calendar-readonly.md`): és l'arxiu del PMS vell.
  {
    icon: IconHistory,
    label: 'Calendari històric',
    path: '/calendari/historic',
    adminOnly: true,
  },
  { icon: IconBed, label: 'Reserves', path: '/reserves' },
  { icon: IconUsers, label: 'Clients', path: '/clients' },
  { icon: IconLuggage, label: 'Viatgers', path: '/viatgers' },
  { icon: IconMessageChatbot, label: 'Miranda', path: '/chat' },
  // Inbox de WhatsApp (§2bis i de `plans/whatsapp-inbox.md`): entre Miranda i Tasques, badge d'obertes.
  {
    icon: MessageCircle,
    label: 'WhatsApp',
    path: '/whatsapp',
    dynamicBadge: 'whatsapp',
    matchSubpaths: true,
  },
  { icon: IconListDetails, label: 'Tasques', path: '/workflows' },
  {
    icon: IconPhoneOff,
    label: 'Precontacte Expedia',
    path: '/precontacte',
    dynamicBadge: 'precontact',
  },
  // Telèfons de Booking (`plans/booking-phone-recovery.md`): el comptador són les urgents (7 dies).
  {
    icon: IconPhonePlus,
    label: 'Precontacte Booking',
    path: '/precontacte-booking',
    dynamicBadge: 'precontactBooking',
  },
  { icon: IconNotebook, label: 'Casos', path: '/casos' },
];

// Grups col·lapsables
const menuGroups: MenuGroup[] = [
  {
    label: 'Operacions',
    icon: IconFileText,
    items: [
      // Runbook 18, F3: tot Mossos és només per a ADMIN (el backend també ho exigeix).
      { icon: IconFileText, label: 'Mossos TXT', path: '/mossos', adminOnly: true },
      { icon: IconForms, label: 'Forms Públics', path: '/forms' },
      { icon: IconUserCircle, label: 'Persons', path: '/persons' },
      { icon: IconUpload, label: 'Upload Factures', path: '/admin/invoices/upload' },
      { icon: IconShoppingCart, label: 'Amazon Import', path: '/admin/invoices/amazon-import' },
      { icon: IconBook, label: 'Manual', path: '/manual' },
    ],
  },
  {
    label: 'Tools',
    icon: IconTool,
    items: [{ icon: IconCast, label: 'Castink', path: '/tools/castink' }],
  },
  {
    label: 'Admin',
    icon: IconSettings,
    adminOnly: true,
    items: [
      { icon: IconAdjustments, label: 'GuestPortal', path: '/admin/guest-portal' },
      { icon: IconFileText, label: 'Contractes', path: '/admin/contracts' },
      { icon: IconKey, label: 'Panys TTLock', path: '/admin/locks' },
      { icon: IconBed, label: 'Habitacions', path: '/admin/rooms' },
      { icon: IconTemperature, label: 'Clima/HVAC', path: '/admin/climate' },
      { icon: IconBell, label: 'Notificacions', path: '/admin/notifications' },
      { icon: IconBug, label: 'Engineering Log', path: '/admin/engineering' },
      { icon: IconGitPullRequest, label: 'Workflows', path: '/admin/workflows', dynamicBadge: 'workflows' },
      { icon: IconListDetails, label: 'Workflow Runs', path: '/admin/workflow-runs' },
      { icon: IconRobot, label: 'Agents IA', path: '/admin/agents' },
      { icon: IconDatabaseImport, label: 'Data Import', path: '/admin/data-import' },
      { icon: IconWorld, label: 'Crawlers', path: '/admin/crawlers' },
      { icon: IconCoin, label: 'Preus Competència', path: '/admin/prices' },
      { icon: IconBuildingBank, label: 'Moviments Bancaris', path: '/admin/bank-movements' },
      { icon: IconCreditCard, label: 'Cobraments Stripe', path: '/admin/cobraments' },
      { icon: IconFileInvoice, label: 'Factures', path: '/admin/billing' },
      {
        icon: IconMessageChatbot,
        label: 'Plantilles Messaging',
        path: '/admin/messaging-templates',
      },
      { icon: IconBrandWhatsapp, label: 'Informe WhatsApp', path: '/admin/whatsapp' },
      { icon: IconSettings, label: 'Configuració', path: '/admin/settings' },
    ],
  },
];

export default function Sidebar({ open, minimized, onClose, onToggleMinimize }: SidebarProps) {
  const location = useLocation();
  const { user } = useAuth();
  const [openGroups, setOpenGroups] = useState<string[]>([]);

  const isAdmin = user?.role === 'ADMIN';
  const visibleMainItems = mainMenuItems.filter((item) => !item.adminOnly || isAdmin);
  // `adminOnly` val per al grup sencer i també per a cada ítem de dins; un grup buit no es pinta.
  const visibleGroups = menuGroups
    .filter((group) => !group.adminOnly || isAdmin)
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.adminOnly || isAdmin),
    }))
    .filter((group) => group.items.length > 0);
  const dynamicBadgeCounts: Record<DynamicBadge, number> = {
    workflows: usePendingWorkflowCount(),
    precontact: usePrecontactCount(),
    precontactBooking: usePrecontactBookingCount(),
    whatsapp: useWhatsappInboxOpenCount(),
  };

  const toggleGroup = (label: string) => {
    setOpenGroups((prev) =>
      prev.includes(label) ? prev.filter((g) => g !== label) : [...prev, label],
    );
  };

  const renderMenuItem = (item: MenuItem, isMobile = false) => {
    const Icon = item.icon;
    const isActive =
      location.pathname === item.path ||
      (!!item.matchSubpaths && location.pathname.startsWith(`${item.path}/`));
    const isDisabled = item.disabled || false;

    return (
      <Link
        key={item.path}
        to={isDisabled ? '#' : item.path}
        className={cn(
          'flex items-center space-x-3 px-3 rounded-lg transition-all duration-200',
          isMobile ? 'py-3' : 'py-2',
          isActive && !isDisabled
            ? 'bg-green-50 text-green-700 font-medium'
            : isDisabled
              ? 'text-gray-400 cursor-not-allowed opacity-50'
              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900',
          minimized && !isMobile && 'justify-center',
        )}
        onClick={(e) => {
          if (isDisabled) {
            e.preventDefault();
          } else if (isMobile) {
            onClose();
          }
        }}
        title={minimized && !isMobile ? item.label : undefined}
      >
        <Icon className={cn('flex-shrink-0', isMobile ? 'w-6 h-6' : 'w-5 h-5')} />
        {(!minimized || isMobile) && (
          <>
            <span className={cn('flex-1', isMobile ? 'text-base' : 'text-sm')}>{item.label}</span>
            {(() => {
              const count = item.dynamicBadge
                ? dynamicBadgeCounts[item.dynamicBadge]
                : (item.badge ?? 0);
              return count > 0 ? (
                <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-xs font-semibold rounded-full bg-yellow-500 text-white">
                  {count}
                </span>
              ) : null;
            })()}
          </>
        )}
      </Link>
    );
  };

  const renderMenuGroup = (group: MenuGroup, isMobile = false) => {
    const Icon = group.icon;
    const isOpen = openGroups.includes(group.label);
    const hasActiveItem = group.items.some((item) => location.pathname === item.path);

    if (minimized && !isMobile) {
      // En mode minimitzat, mostrar només icona del grup
      return (
        <div key={group.label} className="relative group">
          <button
            className={cn(
              'w-full flex justify-center px-3 py-2 rounded-lg transition-colors',
              hasActiveItem ? 'text-green-700' : 'text-gray-500 hover:bg-gray-100',
            )}
            title={group.label}
          >
            <Icon className="w-5 h-5" />
          </button>
        </div>
      );
    }

    return (
      <div key={group.label} className="space-y-1">
        <button
          onClick={() => toggleGroup(group.label)}
          className={cn(
            'w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors',
            hasActiveItem ? 'text-green-700 bg-green-50/50' : 'text-gray-500 hover:bg-gray-100',
          )}
        >
          <div className="flex items-center space-x-3">
            <Icon className="w-5 h-5" />
            <span className="text-sm font-medium">{group.label}</span>
          </div>
          <IconChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
        </button>
        {isOpen && (
          <div className="pl-4 space-y-1">
            {group.items.map((item) => renderMenuItem(item, isMobile))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <aside
        className={cn(
          'hidden md:flex flex-col bg-white border-r border-gray-200 transition-all duration-300 ease-in-out',
          minimized ? 'w-20' : 'w-64',
        )}
      >
        {/* Logo/Header */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          {!minimized && (
            <div className="flex items-center space-x-2">
              <span className="text-2xl">🏨</span>
              <div className="flex flex-col gap-1">
                <span className="font-bold text-lg text-gray-900 leading-tight">Guilleumes</span>
                <VersionBadge />
              </div>
            </div>
          )}
          {minimized && <span className="text-2xl mx-auto">🏨</span>}
        </div>

        {/* Menu Items */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {/* Main Menu */}
          <div className="space-y-1 px-2">{visibleMainItems.map((item) => renderMenuItem(item))}</div>

          {/* Separator */}
          {!minimized && <div className="my-3 mx-4 border-t border-gray-200" />}

          {/* Collapsible Groups */}
          <div className="space-y-1 px-2">
            {visibleGroups.map((group) => renderMenuGroup(group))}
          </div>
        </nav>

        {/* Toggle Minimize Button (Desktop Only) */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={onToggleMinimize}
            className={cn(
              'w-full flex items-center space-x-3 px-3 py-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors',
              minimized && 'justify-center',
            )}
            title={minimized ? 'Expandir' : 'Minimitzar'}
          >
            {minimized ? (
              <IconChevronRight className="w-5 h-5" />
            ) : (
              <>
                <IconChevronLeft className="w-5 h-5" />
                <span className="text-sm">Minimitzar</span>
              </>
            )}
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar (Overlay) */}
      {open && (
        <div className="md:hidden fixed inset-0 z-50">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

          {/* Sidebar Panel */}
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-white shadow-2xl transform transition-transform duration-300 ease-in-out overflow-y-auto">
            {/* Header */}
            <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200 sticky top-0 bg-white z-10">
              <div className="flex items-center space-x-2">
                <span className="text-2xl">🏨</span>
                <span className="font-bold text-lg text-gray-900">Guilleumes</span>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                aria-label="Tancar menú"
              >
                <IconX className="w-5 h-5 text-gray-700" />
              </button>
            </div>

            {/* Menu Items */}
            <nav className="py-4">
              {/* Main Menu */}
              <div className="space-y-1 px-2">
                {visibleMainItems.map((item) => renderMenuItem(item, true))}
              </div>

              {/* Separator */}
              <div className="my-3 mx-4 border-t border-gray-200" />

              {/* Collapsible Groups */}
              <div className="space-y-1 px-2">
                {visibleGroups.map((group) => renderMenuGroup(group, true))}
              </div>
            </nav>
          </aside>
        </div>
      )}
    </>
  );
}
